import { useEffect, useRef, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { twofaService } from "@/services/twofaService";
import { errorMessage } from "@/services/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const box = "rounded-md border border-[#22252F] bg-[#0F1116] p-4";

export const TwoFactorSetup = ({ setupToken, onDone, title = "Configurar autenticação em dois fatores" }) => {
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const requested = useRef(false);

  useEffect(() => {
    // Um único pedido de configuração: cada chamada a /setup gera um novo segredo
    // e invalidaria o QR Code já apresentado (React StrictMode monta duas vezes).
    if (requested.current) return;
    requested.current = true;
    twofaService.setup(setupToken).then(setData).catch((e) => setError(errorMessage(e)));
  }, [setupToken]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await twofaService.enable(code, setupToken);
      setCodes(res.recovery_codes);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (codes) {
    return (
      <div className="space-y-4" data-testid="recovery-codes-panel">
        <p className="text-sm text-white">Guarde estes códigos de recuperação. Cada um pode ser usado uma única vez.</p>
        <div className={`${box} grid grid-cols-2 gap-2 font-mono text-sm text-[#60A5FA]`}>
          {codes.map((c) => <span key={c} data-testid="recovery-code">{c}</span>)}
        </div>
        <Button data-testid="recovery-codes-continue" onClick={onDone}
                className="h-11 w-full rounded-md bg-[#2563EB] text-xs uppercase tracking-[0.15em] hover:bg-[#1D4ED8]">
          Guardei os códigos, continuar
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="twofa-setup-form">
      <div className="flex items-center gap-2 text-sm text-white">
        <ShieldCheck className="h-4 w-4 text-[#60A5FA]" /> {title}
      </div>
      <p className="text-sm text-[#94A3B8]">
        Leia o QR Code na sua aplicação autenticadora (Google Authenticator, Authy, Microsoft Authenticator, Raivo)
        ou introduza a chave manualmente.
      </p>
      {data ? (
        <div className="space-y-3">
          <img data-testid="twofa-qr" alt="QR Code 2FA" className="mx-auto h-44 w-44 rounded-md bg-white p-2"
               src={`data:image/png;base64,${data.qr_png_base64}`} />
          <div className={box}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">Chave manual</p>
            <p data-testid="twofa-manual-secret" className="select-all break-all font-mono text-sm text-white">
              {data.manual_secret}
            </p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[#64748B]">A gerar QR Code…</p>
      )}
      <Input required inputMode="numeric" maxLength={6} data-testid="twofa-setup-code-input"
             value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
             placeholder="Código de 6 dígitos"
             className="h-12 rounded-md border-[#22252F] bg-[#0F1116] text-center font-mono text-lg tracking-[0.4em] text-white" />
      {error ? (
        <p data-testid="twofa-setup-error" className="rounded-md border border-[#EF4444]/40 bg-[#EF4444]/10 px-4 py-3 text-sm text-[#FCA5A5]">
          {error}
        </p>
      ) : null}
      <Button type="submit" disabled={busy || code.length !== 6} data-testid="twofa-setup-submit"
              className="h-12 w-full rounded-md bg-[#2563EB] text-xs uppercase tracking-[0.15em] hover:bg-[#1D4ED8]">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ativar 2FA"}
      </Button>
    </form>
  );
};
